import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChatbotConfig, Project, Stat, ToolConfigWebsiteSearch, User } from '@shared/index';
import { KeyValEntry } from '@shared/KeyValStore';
import { firstValueFrom } from 'rxjs';
import { BotImportApiResponse } from './bot-import.types';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  constructor(private http: HttpClient) { }

  public async historyCleanup(botId: string): Promise<void> {
    await firstValueFrom(this.http.get('/api/toolbox/bot/admin/deleteChathistory/' + botId));
  }

  public async addBot(name: string, projectId?: string): Promise<ChatbotConfig> {
    const body: { name: string, projectId?: string } = { name };
    if (projectId) body.projectId = projectId;
    return await firstValueFrom(this.http.post<ChatbotConfig>("/api/toolbox/bot/admin/createbotconfig", body));
  }

  public async deleteImage(botId: string, variant?: 'open'): Promise<ChatbotConfig> {
    const url = "/api/toolbox/bot/admin/image/" + botId + (variant ? "?variant=" + variant : "");
    return await firstValueFrom(this.http.delete<ChatbotConfig>(url));
  }

  public async reloadImage(botId: string, prompt: string, variant?: 'open'): Promise<ChatbotConfig> {
    return await firstValueFrom(this.http.post<ChatbotConfig>("/api/toolbox/bot/admin/generateImage/" + botId, { prompt, variant }));
  }

  public async setImageFromSkill(botId: string, skillId: string, variant?: 'open'): Promise<ChatbotConfig> {
    return await firstValueFrom(this.http.post<ChatbotConfig>("/api/toolbox/bot/admin/setImageFromSkill/" + botId, { skillId, variant }));
  }

  public async importBot(botConfig: ChatbotConfig): Promise<ChatbotConfig> {
    return await firstValueFrom(this.http.post<ChatbotConfig>("/api/toolbox/bot/admin/importbotconfig", botConfig));
  }

  public async loadDemoBots(): Promise<ChatbotConfig[]> {
    return (await firstValueFrom(this.http.get<{ chatbotConfigs: ChatbotConfig[] }>("/api/toolbox/bot/admin/demoBots"))).chatbotConfigs;
  }

  public async loadTemplateBots(): Promise<ChatbotConfig[]> {
    return (await firstValueFrom(this.http.get<{ chatbotConfigs: ChatbotConfig[] }>("/api/toolbox/bot/admin/templateBots"))).chatbotConfigs;
  }

  public async loadArchiveBots(): Promise<ChatbotConfig[]> {
    return (await firstValueFrom(this.http.get<{ chatbotConfigs: ChatbotConfig[] }>("/api/toolbox/bot/admin/archiveBots"))).chatbotConfigs;
  }

  public async loadTrashBots(): Promise<ChatbotConfig[]> {
    return (await firstValueFrom(this.http.get<{ chatbotConfigs: ChatbotConfig[] }>("/api/toolbox/bot/admin/trashBots"))).chatbotConfigs;
  }

  public async copyBot(botId: string, config: Partial<ChatbotConfig> = {}): Promise<ChatbotConfig> {
    return await firstValueFrom(this.http.post<ChatbotConfig>('/api/toolbox/bot/admin/copybot/' + botId, config));
  }

  public async unarchiveBot(botId: string): Promise<void> {
    await this.updateBotField(botId, "archived", false);
  }

  public async archiveBot(botId: string): Promise<void> {
    await this.updateBotField(botId, "archived", true);
  }

  public async untrashBot(botId: string): Promise<void> {
    await this.updateBotField(botId, "thisIsTrash", false);
  }

  public async trashBot(botId: string): Promise<void> {
    await this.updateBotField(botId, "thisIsTrash", true);
  }

  public async deleteBot(botId: string): Promise<void> {
    await firstValueFrom(this.http.post('/api/toolbox/bot/admin/deletebotconfig/' + botId, {}));
  }

  public async updateBotField(botId: string, field: keyof ChatbotConfig, value: ChatbotConfig[typeof field]): Promise<ChatbotConfig> {
    if (field == "tagList") {
      (value as string[]).sort((a: string, b: string) => a.localeCompare(b));
    }
    return await firstValueFrom(this.http.post<ChatbotConfig>('/api/toolbox/bot/admin/updatebotconfigfield/' + botId, { field, value }));
  }

  public async addBotAsUrl(botId: string, url: string) {
    return await firstValueFrom(this.http.post("/api/toolbox/bot/admin/boturl/add/" + botId, { url }));
  }

  public async addWebsitesearch(botId: string, toolConfig: ToolConfigWebsiteSearch) {
    return await firstValueFrom(this.http.post("/api/toolbox/bot/admin/addWebsitesearch/" + botId, { toolConfig }));
  }

  public async getUserStats(botId: string): Promise<Stat[]> {
    return await firstValueFrom(this.http.get<Stat[]>('/api/toolbox/bot/admin/getStats/' + botId));
  }

  public async deleteUserStat(botId: string, stat: string): Promise<{ acknowledged: boolean, deletedCount: number }> {
    return await firstValueFrom(this.http.delete<{ acknowledged: boolean, deletedCount: number }>('/api/toolbox/bot/admin/deleteStat/' + botId + '/' + stat));
  }

  public async getUsers(): Promise<User<string, string>[]> {
    const unsortedUsers = (await firstValueFrom(this.http.get<{ status: string, users: User<string, string>[] }>('/api/toolbox/bot/admin/users'))).users;
    return unsortedUsers.toSorted((a, b) => a.name.localeCompare(b.name));
  }

  public async loadBotConfig(id: string): Promise<ChatbotConfig | null> {
    try {
      const botConfig = await firstValueFrom(this.http.get<ChatbotConfig>("/api/toolbox/bot/admin/botconfig/" + id));
      return botConfig;
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  public async getUserValues(botId: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return await firstValueFrom(this.http.get<(KeyValEntry<any> & { context: { guestId?: string } })[]>(`/api/toolbox/bot/admin/getKeyValData/${botId}`));
  }

  public async updateProjectField(projectId: string, field: keyof Project, value: Project[typeof field]): Promise<Project> {
    return await firstValueFrom(this.http.post<Project>('/api/project' + projectId, { field, value }));
  }

  public async copyIfThen(botId: string, toolId: string): Promise<ChatbotConfig> {
    return await firstValueFrom(this.http.get<ChatbotConfig>('/api/toolbox/bot/admin/copyToolConfig/' + botId + '/' + toolId));
  }

  public async importBotFromZip(formData: FormData): Promise<BotImportApiResponse> {
    return await firstValueFrom(this.http.post<BotImportApiResponse>('/api/toolbox/bot/admin/importaszip', formData));
  }
}
